/**
 * usePlatform Hook
 * Story 6.1: Implement Unified Toolbar Wrapper
 *
 * Detects the user's operating system for platform-aware UI.
 * Used for:
 * - AC-6.1.5: Platform-aware modifier display (macOS: ⌘, Windows/Linux: Ctrl)
 * - AC-6.1.4: Keyboard shortcut handling
 */
import { useMemo } from 'react';

export interface PlatformInfo {
  /** True if user is on macOS */
  isMac: boolean;
  /** Text modifier name: 'Cmd' or 'Ctrl' */
  modifier: 'Cmd' | 'Ctrl';
  /** Symbol for modifier: '⌘' or 'Ctrl' */
  modifierSymbol: string;
  /** Event property to check: 'metaKey' or 'ctrlKey' */
  modifierKey: 'metaKey' | 'ctrlKey';
}

/**
 * Returns platform-specific information for UI and keyboard handling
 *
 * @example
 * const { isMac, modifierSymbol } = usePlatform();
 * // On macOS: modifierSymbol = '⌘'
 * // On Windows: modifierSymbol = 'Ctrl'
 */
export function usePlatform(): PlatformInfo {
  return useMemo(() => {
    const isMac = typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad|iPod/.test(navigator.platform);

    return {
      isMac,
      modifier: isMac ? 'Cmd' : 'Ctrl',
      modifierSymbol: isMac ? '⌘' : 'Ctrl',
      modifierKey: isMac ? 'metaKey' : 'ctrlKey',
    };
  }, []);
}
